import { ApiQueryPromise } from '@/types';
import { getResponseJwtHeader } from '@/utils/web';
import { useQuery } from '@tanstack/react-query';
import { fetchComposer } from '@nf/utils-common/compose-fetch';
import { cloudUrlParser } from '@/utils/aws-domain-util';
import queryKeys from '@/constants/query-keys';
import { useSiteStore } from '@/store/site';
import { getAuthToken } from '@/store/auth';
import { useExternalScript } from '@nf/utils-common/hooks';
import { siteService } from '@/services/site.service';
import { useEffect, useState } from 'react';
import { useGetSiteConfigs } from './use-site-configs';
import { useAccountInfo } from './account';
import { useStreamingStore } from '@/store/streaming';
import { EsportChannelModel } from '@/services/streaming.service';
import { useTranslations } from 'next-intl';

export interface EsportStreamingUrlResponse {
	Sort: number
	Url: string
	SourceType: number
	Language: number
}

export interface EsportPreMatchRequest {
	matchid?: number
	leagueName?: string
	homeName?: string
	awayName?: string
}

const getEsportStreamingData = async (matchid: number): Promise<ApiQueryPromise<EsportStreamingUrlResponse[]>> => {
	const authToken = getAuthToken();
	const apiDomain = useSiteStore.getState().apiDomain;
	const body = JSON.stringify({ mid: matchid });
	const response = await fetchComposer.postWithBearer(cloudUrlParser(`${apiDomain.ctcdDomain}/ESportsWeb/GetStreamingInfo`), { body: body })(authToken);
	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}

	return {
		data: await response.json(),
		jwtToken: getResponseJwtHeader(response)
	};
};

export const useGetEsportStreamingData = (matchid: number) => {

	const { data, isLoading, isError, error, refetch } = useQuery({
		queryKey: [queryKeys.GET_ESPORT_STREAMING],
		queryFn: () => getEsportStreamingData(matchid),
		cacheTime: 0,
		enabled: false,
		refetchOnWindowFocus: false,
	});

	return {
		data: data?.data,
		isLoading,
		isError,
		error,
		refetch
	};
};

export const useEsportStreamingControl = (matchid: number) => {
	const t = useTranslations('General');
	const [
		streamingSrc,
		isRefresh,
		setEsportChannelList,
		setStreamingType,
		streamingType,
	] = useStreamingStore(
		state => [
			state.streamingSrc,
			state.isRefresh,
			state.setEsportChannelList,
			state.setStreamingType,
			state.streamingType
		]
	);

	const [urlList, seturlList] = useState(Array<EsportStreamingUrlResponse>);
	const { SiteConfigsData } = useGetSiteConfigs();
	const { user } = useAccountInfo();
	const getHlsExternalJs = useExternalScript(`${siteService.combineImagePath('/Js/video/hls.min.js', true, SiteConfigsData)}`);
	const { refetch } = useGetEsportStreamingData(matchid);
	const filterTwitch = (data: Array<EsportChannelModel>) => {
		let filterUrlList = data;

		const CanSeeESTwitchStreaming = user?.CanSeeESTwitchStreaming;

		(!CanSeeESTwitchStreaming) ?
			streamingSrc == '14' ? filterUrlList = data.filter(item => item.sourceType != 5)
				: filterUrlList = data
			: filterUrlList = data;
		return filterUrlList;
	};

	useEffect(() => {
		if (getHlsExternalJs == 'ready') {
			refetch().then((res) => {
				const StreamingDatas = res.data?.data.Data;
				const esportStreamingSourceList = Array<EsportChannelModel>();
				if (StreamingDatas) {
					StreamingDatas.forEach((item) => {
						esportStreamingSourceList.push({
							value: item.Sort.toString(),
							url: item.Url,
							lang: item.Language,
							label: `${t('lbl_Channels')} ${item.Sort}`,
							sourceType: item.SourceType
						})
					});
					setEsportChannelList(filterTwitch(esportStreamingSourceList));
					if (esportStreamingSourceList.length > 0) {
						setStreamingType('Streaming');
					}
				}
			});
		};
	}, [matchid, getHlsExternalJs, isRefresh, streamingType]);

	return urlList;
};

export const getEsportPreMatchUrl = async (requestData: EsportPreMatchRequest): Promise<ApiQueryPromise<string>> => {
	const authToken = getAuthToken();
	const apiDomain = useSiteStore.getState().apiDomain;
	const body = JSON.stringify({
		MatchId: requestData.matchid,
		LeagueName: requestData.leagueName,
		HomeName: requestData.homeName,
		AwayName: requestData.awayName
	});
	const response = await fetchComposer.postWithBearer(cloudUrlParser(`${apiDomain.ctcdDomain}/api/Streaming/GetEsportPreMatchUrl`), { body: body })(authToken);
	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}
	return {
		data: await response.json(),
		jwtToken: getResponseJwtHeader(response)
	};
};